import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Input,
    Button,
    Space,
    Typography,
    Spin,
    message,
    Modal,
    Select,
    Empty,
    Tabs,
    Tooltip,
} from 'antd';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { HiOutlineClipboardList, HiOutlineEye } from 'react-icons/hi';
import { FaXmark } from 'react-icons/fa6';
import { FormCanvas } from './components/FormCanvas';
import { FieldConfigDrawer } from './components/FieldConfigDrawer';
import { SectionManagerModal } from './components/SectionManagerModal';
import { getFieldTypeMeta } from './components/FieldTypeSelect';
import {
    SaveOutlined,
    EyeOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    PlusOutlined,
    FolderOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import { FormPreviewV2 } from './components/FormPreviewV2';
import {
    useFormBuilder,
    validateFormFieldsDetailed,
    ensureFixedSystemFields,
    isFixedSystemFieldId,
} from './hooks/useFormBuilder';
import { FormField_V2, FieldType_V2 } from '../../common/typescript/models/CollectFormV2';
import type { CollectForm_Base } from '../../common/typescript/models/CollectForm';
import type { CollectFormTemplate } from '../../common/typescript/models/CollectFormTemplate';
import { CollectFormTemplateAPIs } from '../../common/typescript/apis/CollectFormTemplateAPIs';
import { useProjects } from '../FreePhotoScreen/hooks/useProjects';
import { currentProjectAtom } from '../../stores/ProjectStore';
import Routes from '../../routes/routes';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

export interface FormBuilderScreenProps {
    mode?: 'template' | 'project_form';
    initialProjectId?: number;
    initialFormUuid?: string;
    isModal?: boolean;
    onCloseModal?: () => void;
    onSavedForm?: (form: CollectForm_Base) => void;
}

export const FormBuilderScreen: React.FC<FormBuilderScreenProps> = ({
    mode,
    initialProjectId,
    initialFormUuid,
    isModal = false,
    onCloseModal,
    onSavedForm,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { templateId } = useParams<{ templateId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryProjectId = searchParams.get('projectId')
        ? Number(searchParams.get('projectId'))
        : undefined;
    const queryFormUuid = searchParams.get('formUuid') || undefined;

    const isProjectFormMode =
        mode === 'project_form' ||
        Boolean(initialProjectId) ||
        Boolean(initialFormUuid) ||
        Boolean(queryProjectId) ||
        Boolean(queryFormUuid) ||
        location.pathname.startsWith(Routes.COLLECT_FORM_DESIGN);

    const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(
        initialProjectId || queryProjectId,
    );
    const [activeFormUuid, setActiveFormUuid] = useState<string | undefined>(
        initialFormUuid || queryFormUuid,
    );

    const currentProject = useAtomValue(currentProjectAtom);
    const { projects, loading: projectsLoading } = useProjects();
    const [activeModalTab, setActiveModalTab] = useState<'fields' | 'preview'>('fields');

    const {
        formSchema,
        metadata,
        uploadedFiles,
        templateUuid,
        loading,
        addField,
        updateField,
        updateFieldAtIndex,
        deleteField,
        deleteFieldAtIndex,
        reorderFields,
        saveTemplate,
        saveProjectForm,
        loadTemplate,
        loadProjectForm,
        resetForm,
        setMetadata,
        setFormSchema,
        addSection,
        updateSection,
        deleteSection,
    } = useFormBuilder();

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [selectedField, setSelectedField] = useState<FormField_V2 | null>(null);
    const [editingFieldIndex, setEditingFieldIndex] = useState<number | undefined>(undefined);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [step1Attempted, setStep1Attempted] = useState(false);
    const [localSqliteFiles] = useState<File[]>([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [sectionModalVisible, setSectionModalVisible] = useState(false);
    const [templatePickerVisible, setTemplatePickerVisible] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState<CollectFormTemplate[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    useEffect(() => {
        if (initialProjectId) {
            setSelectedProjectId(initialProjectId);
        } else if (queryProjectId) {
            setSelectedProjectId(queryProjectId);
        }
    }, [initialProjectId, queryProjectId]);

    // Load form/template if editing or reset if new
    useEffect(() => {
        const targetFormUuid = initialFormUuid || queryFormUuid;
        if (isProjectFormMode) {
            if (targetFormUuid) {
                setActiveFormUuid(targetFormUuid);
                setCurrentStep(2);
                loadProjectForm(targetFormUuid).then((loadedForm) => {
                    if (loadedForm?.project_id) {
                        setSelectedProjectId(loadedForm.project_id);
                    }
                });
            } else {
                setActiveFormUuid(undefined);
                setCurrentStep(1);
                setStep1Attempted(false);
                resetForm();
            }
        } else {
            if (templateId && templateId !== 'new') {
                setCurrentStep(2);
                loadTemplate(templateId);
            } else {
                setCurrentStep(1);
                setStep1Attempted(false);
                resetForm();
            }
        }
    }, [isProjectFormMode, initialFormUuid, queryFormUuid, templateId, loadProjectForm, loadTemplate, resetForm]);

    const handleNextStep = () => {
        setStep1Attempted(true);
        if (isProjectFormMode && !selectedProjectId) {
            message.error('Vui lòng chọn dự án cho biểu mẫu');
            return;
        }
        if (!metadata.name.trim()) {
            message.error('Vui lòng nhập tên biểu mẫu');
            return;
        }
        setStep1Attempted(false);
        setCurrentStep(2);
    };

    const handleOpenTemplatePicker = async () => {
        setTemplatePickerVisible(true);
        try {
            setTemplatesLoading(true);
            const list = await CollectFormTemplateAPIs.remote_getTemplates();
            setAvailableTemplates(list || []);
        } catch (error) {
            console.error('[FormBuilderScreen] Failed to load templates:', error);
            message.error('Không thể tải danh sách mẫu biểu mẫu');
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleApplyTemplate = (tpl: CollectFormTemplate) => {
        if (tpl.content) {
            const mergedFields = ensureFixedSystemFields(tpl.content.fields || []);
            setFormSchema({
                ...tpl.content,
                fields: mergedFields,
                metadata: {
                    ...(tpl.content.metadata || {}),
                    version: '2.0.0',
                    createdWith: 'GeoCloudWeb',
                },
            });
            setMetadata((prev) => ({
                name: prev.name.trim() ? prev.name : tpl.name,
                description: prev.description.trim() ? prev.description : tpl.description || '',
            }));
            setTemplatePickerVisible(false);
            setStep1Attempted(false);
            setCurrentStep(2);
            message.success(`Đã áp dụng mẫu "${tpl.name}" (${mergedFields.length} trường)`);
            return;
        }
        setMetadata((prev) => ({
            name: prev.name.trim() ? prev.name : tpl.name,
            description: prev.description.trim() ? prev.description : tpl.description || '',
        }));
        setTemplatePickerVisible(false);
        setStep1Attempted(false);
        setCurrentStep(2);
        message.success(`Đã áp dụng mẫu "${tpl.name}"`);
    };

    const handleAddFieldClick = () => {
        setSelectedField(null);
        setEditingFieldIndex(undefined);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    const handleQuickAddField = () => {
        const nextIndex = formSchema.fields.length + 1;
        let candidateId = `truong_${nextIndex}`;
        const existingIds = new Set(formSchema.fields.map((f) => (f.id || '').trim().toLowerCase()));
        let suffix = nextIndex;
        while (existingIds.has(candidateId.toLowerCase())) {
            suffix += 1;
            candidateId = `truong_${suffix}`;
        }
        addField({
            id: candidateId,
            label: `Trường dữ liệu ${nextIndex}`,
            type: FieldType_V2.TEXT,
            required: false,
            placeholder: '',
        });
    };

    const handleFieldEdit = (field: FormField_V2, index?: number) => {
        setSelectedField(field);
        setEditingFieldIndex(index);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    const handleFieldSave = (field: FormField_V2) => {
        if (drawerMode === 'create') {
            addField(field);
            message.success(`Đã thêm trường "${field.label}"`);
        } else if (editingFieldIndex !== undefined) {
            updateFieldAtIndex(editingFieldIndex, field);
            message.success(`Đã cập nhật trường "${field.label}"`);
        } else {
            updateField(selectedField?.id || field.id, field);
            message.success(`Đã cập nhật trường "${field.label}"`);
        }
        setDrawerVisible(false);
        setSelectedField(null);
        setEditingFieldIndex(undefined);
    };

    const handleFieldDelete = (fieldId: string, index?: number) => {
        if (isFixedSystemFieldId(fieldId)) {
            const norm = (fieldId || '').trim().toLowerCase();
            const firstIdx = formSchema.fields.findIndex((f) => (f.id || '').trim().toLowerCase() === norm);
            if (index === undefined || index === firstIdx) {
                message.warning(`Trường cố định "${fieldId}" không thể xóa`);
                return;
            }
        }
        if (index !== undefined) {
            deleteFieldAtIndex(index);
        } else {
            deleteField(fieldId);
        }
        message.success('Đã xóa trường');
    };

    const handleSave = async () => {
        setSubmitAttempted(true);
        if (!metadata.name.trim()) {
            setStep1Attempted(true);
            setCurrentStep(1);
            message.error('Vui lòng nhập tên biểu mẫu');
            return;
        }
        const validation = validateFormFieldsDetailed(formSchema.fields);
        if (!validation.isValid && activeModalTab !== 'fields') {
            setActiveModalTab('fields');
        }

        if (isProjectFormMode) {
            const saved = await saveProjectForm(selectedProjectId, activeFormUuid);
            if (saved) {
                setSubmitAttempted(false);
                setActiveFormUuid(saved.uuid);
                if (onSavedForm) {
                    onSavedForm(saved);
                }
                if (!isModal && !activeFormUuid && saved.uuid) {
                    setSearchParams(
                        {
                            formUuid: saved.uuid,
                            projectId: String(selectedProjectId || saved.project_id),
                        },
                        { replace: true },
                    );
                }
            }
            return;
        }

        const success = await saveTemplate();
        if (success && (!templateId || templateId === 'new') && templateUuid) {
            navigate(`/collect-form-template/design/${templateUuid}`);
        }
    };

    const handleBack = () => {
        if (isModal && onCloseModal) {
            onCloseModal();
            return;
        }
        if (isProjectFormMode) {
            if (searchParams.get('returnTo') === 'project' && selectedProjectId) {
                navigate(`${Routes.PROJECT}/${selectedProjectId}`);
            } else {
                navigate(Routes.COLLECT_FORMS);
            }
            return;
        }
        navigate(Routes.COLLECT_FORM_TEMPLATES);
    };

    const handlePreview = () => {
        setPreviewVisible(true);
    };

    const headerTitle = isProjectFormMode
        ? activeFormUuid
            ? 'Chỉnh sửa biểu mẫu dự án'
            : 'Tạo biểu mẫu trong dự án'
        : templateId && templateId !== 'new'
        ? 'Chỉnh sửa mẫu biểu mẫu'
        : 'Thiết kế mẫu biểu mẫu';

    const resolvedProjectName =
        (currentProject?.id === selectedProjectId ? currentProject?.name : undefined) ||
        projects.find((p) => p.id === selectedProjectId)?.name ||
        currentProject?.name ||
        (selectedProjectId ? `Dự án #${selectedProjectId}` : '');

    return (
        <Spin spinning={loading}>
            {isModal ? (
                /* =====================================================================
                 * GEOCOLLECT PANEL/MODAL MODE (design.md Specification)
                 * ===================================================================== */
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#ffffff',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        maxHeight: '86vh',
                    }}
                >
                    {/* 1. Geocollect-Standard Header (UI_ModalHeader) */}
                    <div
                        style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            borderBottom: '1px solid #e2e8f0',
                            padding: '14px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
                            {currentStep === 2 && (
                                <Tooltip title="Quay lại Bước 1 (Chỉnh sửa Tên biểu mẫu & Mô tả)">
                                    <Button
                                        size="small"
                                        icon={<ArrowLeftOutlined />}
                                        onClick={() => setCurrentStep(1)}
                                        style={{
                                            borderRadius: 6,
                                            height: 32,
                                            padding: '0 10px',
                                            fontWeight: 500,
                                            color: '#334155',
                                            borderColor: '#cbd5e1',
                                            background: '#ffffff',
                                            flexShrink: 0,
                                        }}
                                    >
                                        Quay lại
                                    </Button>
                                </Tooltip>
                            )}

                            <div
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 8,
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                                    flexShrink: 0,
                                }}
                            >
                                <HiOutlineClipboardList size={18} />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {currentStep === 2 && metadata.name.trim()
                                            ? metadata.name.trim()
                                            : headerTitle}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: '#2563eb',
                                            background: '#eff6ff',
                                            border: '1px solid #bfdbfe',
                                            borderRadius: 999,
                                            padding: '1px 8px',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {currentStep === 1 ? 'Bước 1/2: Thông tin chung' : 'Bước 2/2: Trường dữ liệu'}
                                    </span>
                                </div>

                                {currentStep === 2 ? (
                                    <span
                                        style={{
                                            fontSize: 12,
                                            color: '#475569',
                                            lineHeight: 1.35,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {metadata.description.trim()
                                            ? metadata.description.trim()
                                            : 'Chưa có mô tả biểu mẫu'}
                                        {isProjectFormMode && selectedProjectId ? (
                                            <span style={{ color: '#64748b' }}>
                                                {' • '}Dự án:{' '}
                                                <strong style={{ color: '#475569', fontWeight: 500 }}>
                                                    {resolvedProjectName}
                                                </strong>{' '}
                                                (#{selectedProjectId})
                                            </span>
                                        ) : null}
                                    </span>
                                ) : (
                                    isProjectFormMode &&
                                    selectedProjectId && (
                                        <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.3 }}>
                                            Dự án:{' '}
                                            <strong style={{ color: '#475569', fontWeight: 500 }}>
                                                {resolvedProjectName}
                                            </strong>{' '}
                                            (#{selectedProjectId})
                                        </span>
                                    )
                                )}
                            </div>
                        </div>

                        <Tooltip title="Đóng">
                            <button
                                type="button"
                                onClick={handleBack}
                                aria-label="Đóng"
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    border: '1px solid #e2e8f0',
                                    background: '#ffffff',
                                    color: '#64748b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    flexShrink: 0,
                                }}
                            >
                                <FaXmark size={14} />
                            </button>
                        </Tooltip>
                    </div>

                    {/* 2. Modal Body: Step 1 Panel (Metadata) OR Step 2 Panel (Fields & Preview) */}
                    <div
                        style={{
                            padding: '16px 20px 14px 20px',
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {currentStep === 1 ? (
                            /* STEP 1 PANEL: Tên biểu mẫu & Mô tả */
                            <div
                                style={{
                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 10,
                                    padding: '18px 20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 14,
                                }}
                            >
                                {isProjectFormMode && !initialProjectId && !activeFormUuid && (
                                    <div>
                                        <div
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                marginBottom: 6,
                                            }}
                                        >
                                            Dự án <span style={{ color: '#dc2626' }}>*</span>
                                        </div>
                                        <Select
                                            size="middle"
                                            style={{ width: '100%' }}
                                            placeholder="Chọn dự án để tạo biểu mẫu"
                                            value={selectedProjectId}
                                            onChange={(val) => setSelectedProjectId(val)}
                                            loading={projectsLoading}
                                            showSearch
                                            optionFilterProp="label"
                                            options={projects.map((p) => ({
                                                value: p.id,
                                                label: `${p.name} (#${p.id})`,
                                            }))}
                                        />
                                    </div>
                                )}

                                <div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            marginBottom: 6,
                                        }}
                                    >
                                        Tên biểu mẫu <span style={{ color: '#dc2626' }}>*</span>
                                    </div>
                                    <Input
                                        size="middle"
                                        status={
                                            (step1Attempted || submitAttempted) && !metadata.name.trim()
                                                ? 'error'
                                                : undefined
                                        }
                                        placeholder="Nhập tên biểu mẫu (ví dụ: Mẫu Kiểm kê Rừng sản xuất)"
                                        value={metadata.name}
                                        onChange={(e) => setMetadata({ ...metadata, name: e.target.value })}
                                        onPressEnter={handleNextStep}
                                        style={{ borderRadius: 6 }}
                                    />
                                    {(step1Attempted || submitAttempted) && !metadata.name.trim() && (
                                        <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 4 }}>
                                            Vui lòng nhập tên biểu mẫu
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            marginBottom: 6,
                                        }}
                                    >
                                        Mô tả
                                    </div>
                                    <TextArea
                                        rows={3}
                                        placeholder="Nhập mô tả mục đích hoặc hướng dẫn thu thập dữ liệu cho biểu mẫu"
                                        value={metadata.description}
                                        onChange={(e) =>
                                            setMetadata({ ...metadata, description: e.target.value })
                                        }
                                        style={{ borderRadius: 6 }}
                                    />
                                </div>

                                <div
                                    style={{
                                        fontSize: 12,
                                        color: '#64748b',
                                        background: '#ffffff',
                                        border: '1px dashed #cbd5e1',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        lineHeight: 1.45,
                                    }}
                                >
                                    Nhập <strong>Tên biểu mẫu</strong> và <strong>Mô tả</strong> sau đó nhấn{' '}
                                    <strong>Tiếp tục</strong> để thiết lập các trường dữ liệu ở Bước 2, hoặc nhấn{' '}
                                    <strong>Chọn từ Template</strong> để áp dụng nhanh mẫu có sẵn.
                                </div>
                            </div>
                        ) : (
                            /* STEP 2 PANEL: Các trường dữ liệu & Xem trước */
                            <Tabs
                                size="small"
                                activeKey={activeModalTab}
                                onChange={(k) => setActiveModalTab(k as 'fields' | 'preview')}
                                tabBarExtraContent={
                                    <Space size={6}>
                                        <Button
                                            size="small"
                                            icon={<FolderOutlined />}
                                            onClick={() => setSectionModalVisible(true)}
                                            style={{ borderRadius: 6, fontSize: 12 }}
                                        >
                                            Quản lý phần ({formSchema.sections?.length || 0})
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={handleAddFieldClick}
                                            style={{
                                                borderRadius: 6,
                                                fontSize: 12,
                                                fontWeight: 500,
                                                background: '#3b82f6',
                                            }}
                                        >
                                            Thêm trường
                                        </Button>
                                    </Space>
                                }
                                items={[
                                    {
                                        key: 'fields',
                                        label: (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                <HiOutlineClipboardList size={15} />
                                                <span>Các trường dữ liệu ({formSchema.fields.length})</span>
                                            </span>
                                        ),
                                        children: (
                                            <div
                                                style={{
                                                    maxHeight: '50vh',
                                                    minHeight: 220,
                                                    overflowY: 'auto',
                                                    paddingRight: 2,
                                                }}
                                            >
                                                <FormCanvas
                                                    fields={formSchema.fields}
                                                    onFieldEdit={handleFieldEdit}
                                                    onFieldUpdate={updateField}
                                                    onFieldUpdateAtIndex={updateFieldAtIndex}
                                                    onFieldDelete={handleFieldDelete}
                                                    onFieldReorder={reorderFields}
                                                    onQuickAddField={handleQuickAddField}
                                                />
                                            </div>
                                        ),
                                    },
                                    {
                                        key: 'preview',
                                        label: (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                <HiOutlineEye size={15} />
                                                <span>Xem trước</span>
                                            </span>
                                        ),
                                        children: (
                                            <div
                                                style={{
                                                    maxHeight: '50vh',
                                                    minHeight: 220,
                                                    overflowY: 'auto',
                                                    padding: 12,
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <FormPreviewV2
                                                    schema={formSchema}
                                                    sqliteFiles={localSqliteFiles}
                                                />
                                            </div>
                                        ),
                                    },
                                ]}
                            />
                        )}
                    </div>

                    {/* 3. Geocollect-Standard Footer (UI_ModalFooter) */}
                    <div
                        style={{
                            borderTop: '1px solid #e2e8f0',
                            background: '#f8fafc',
                            padding: '12px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 10,
                        }}
                    >
                        <Space size={8} wrap>
                            {isProjectFormMode && (
                                <Button
                                    size="middle"
                                    icon={<CopyOutlined />}
                                    onClick={handleOpenTemplatePicker}
                                    style={{ borderRadius: 6, fontSize: 13 }}
                                >
                                    Chọn từ Template
                                </Button>
                            )}
                        </Space>

                        <Space size={8}>
                            <Button
                                size="middle"
                                onClick={handleBack}
                                style={{ borderRadius: 6, fontSize: 13 }}
                            >
                                Hủy
                            </Button>
                            {currentStep === 1 ? (
                                <Button
                                    type="primary"
                                    size="middle"
                                    onClick={handleNextStep}
                                    style={{
                                        borderRadius: 6,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        background: '#3b82f6',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                    }}
                                >
                                    <span>Tiếp tục</span>
                                    <ArrowRightOutlined style={{ fontSize: 12 }} />
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    size="middle"
                                    icon={<SaveOutlined />}
                                    onClick={handleSave}
                                    loading={loading}
                                    style={{
                                        borderRadius: 6,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        background: '#3b82f6',
                                    }}
                                >
                                    {isProjectFormMode
                                        ? activeFormUuid
                                            ? 'Cập nhật biểu mẫu'
                                            : 'Tạo biểu mẫu'
                                        : 'Lưu biểu mẫu'}
                                </Button>
                            )}
                        </Space>
                    </div>
                </div>
            ) : (
                /* =====================================================================
                 * FULL-PAGE ROUTE MODE (/collect-form/design & /collect-form-template/design)
                 * ===================================================================== */
                <Layout style={{ height: '100vh', overflow: 'hidden' }}>
                    <Header
                        style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            padding: '12px 24px',
                            height: 'auto',
                            lineHeight: 'normal',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 12,
                        }}
                    >
                        <Space size={12}>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={handleBack}
                                style={{ borderRadius: 6 }}
                            >
                                Quay lại
                            </Button>
                            <Title level={4} style={{ margin: 0, color: '#1e293b', fontSize: 17 }}>
                                {headerTitle}
                            </Title>
                        </Space>

                        <Space wrap>
                            {isProjectFormMode && (
                                <Button icon={<CopyOutlined />} onClick={handleOpenTemplatePicker} style={{ borderRadius: 6 }}>
                                    Chọn từ Template
                                </Button>
                            )}
                            <Button icon={<FolderOutlined />} onClick={() => setSectionModalVisible(true)} style={{ borderRadius: 6 }}>
                                Quản lý phần
                            </Button>
                            <Button icon={<EyeOutlined />} onClick={handlePreview} style={{ borderRadius: 6 }}>
                                Xem trước
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={loading}
                                style={{ borderRadius: 6, background: '#3b82f6', fontWeight: 500 }}
                            >
                                {isProjectFormMode
                                    ? activeFormUuid
                                        ? 'Cập nhật biểu mẫu'
                                        : 'Tạo biểu mẫu'
                                    : 'Lưu biểu mẫu'}
                            </Button>
                        </Space>
                    </Header>

                    <Layout style={{ flex: 1, minHeight: 0 }}>
                        <Content style={{ padding: '20px 24px', background: '#f8fafc', overflowY: 'auto' }}>
                            <Space direction="vertical" style={{ width: '100%', maxWidth: 1080, margin: '0 auto', display: 'flex' }} size="middle">
                                <Card
                                    style={{
                                        borderRadius: 10,
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                                    }}
                                >
                                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                        {isProjectFormMode && (
                                            <div>
                                                <Text strong style={{ color: '#1e293b' }}>Dự án *</Text>
                                                <Select
                                                    size="middle"
                                                    style={{ width: '100%', marginTop: 6 }}
                                                    placeholder="Chọn dự án để tạo biểu mẫu"
                                                    value={selectedProjectId}
                                                    onChange={(val) => setSelectedProjectId(val)}
                                                    loading={projectsLoading}
                                                    disabled={Boolean(initialProjectId) || Boolean(activeFormUuid)}
                                                    showSearch
                                                    optionFilterProp="label"
                                                    options={projects.map((p) => ({
                                                        value: p.id,
                                                        label: `${p.name} (#${p.id})`,
                                                    }))}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <Text strong style={{ color: '#1e293b' }}>Tên biểu mẫu *</Text>
                                            <Input
                                                size="middle"
                                                status={submitAttempted && !metadata.name.trim() ? 'error' : undefined}
                                                placeholder="Nhập tên biểu mẫu"
                                                value={metadata.name}
                                                onChange={(e) => setMetadata({ ...metadata, name: e.target.value })}
                                                style={{ marginTop: 6, borderRadius: 6 }}
                                            />
                                            {submitAttempted && !metadata.name.trim() && (
                                                <div style={{ fontSize: 11, color: '#dc2626', marginTop: 3 }}>
                                                    Vui lòng nhập tên biểu mẫu
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <Text strong style={{ color: '#1e293b' }}>Mô tả</Text>
                                            <TextArea
                                                rows={2}
                                                placeholder="Nhập mô tả biểu mẫu"
                                                value={metadata.description}
                                                onChange={(e) =>
                                                    setMetadata({ ...metadata, description: e.target.value })
                                                }
                                                style={{ marginTop: 6, borderRadius: 6 }}
                                            />
                                        </div>
                                    </Space>
                                </Card>

                                <Card
                                    style={{
                                        borderRadius: 10,
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                                    }}
                                    title={
                                        <Space>
                                            <span style={{ color: '#1e293b', fontWeight: 600 }}>
                                                Các trường dữ liệu ({formSchema.fields.length})
                                            </span>
                                        </Space>
                                    }
                                    extra={
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleAddFieldClick}
                                            style={{ borderRadius: 6, background: '#3b82f6' }}
                                        >
                                            Thêm trường
                                        </Button>
                                    }
                                >
                                    <FormCanvas
                                        fields={formSchema.fields}
                                        onFieldEdit={handleFieldEdit}
                                        onFieldUpdate={updateField}
                                        onFieldUpdateAtIndex={updateFieldAtIndex}
                                        onFieldDelete={handleFieldDelete}
                                        onFieldReorder={reorderFields}
                                        onQuickAddField={handleQuickAddField}
                                    />
                                </Card>
                            </Space>
                        </Content>
                    </Layout>
                </Layout>
            )}

            {/* Field Configuration Drawer */}
            <FieldConfigDrawer
                visible={drawerVisible}
                field={selectedField}
                mode={drawerMode}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedField(null);
                }}
                onSave={handleFieldSave}
                existingFieldIds={formSchema.fields.map((f) => f.id)}
                uploadedDatabases={uploadedFiles}
                sqliteFiles={localSqliteFiles}
                sections={formSchema.sections || []}
            />

            <Modal
                title="Xem trước biểu mẫu"
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width={760}
                centered
            >
                <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                    <FormPreviewV2
                        schema={formSchema}
                        sqliteFiles={localSqliteFiles}
                    />
                </div>
            </Modal>

            <SectionManagerModal
                visible={sectionModalVisible}
                sections={formSchema.sections || []}
                onClose={() => setSectionModalVisible(false)}
                onAdd={addSection}
                onUpdate={updateSection}
                onDelete={deleteSection}
            />

            {/* Template Picker Modal (Card Grid Layout) */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 6px rgba(59, 130, 246, 0.28)',
                            }}
                        >
                            <CopyOutlined style={{ fontSize: 15 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                                Chọn từ mẫu biểu mẫu
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>
                                Chọn một mẫu có sẵn để khởi tạo nhanh danh sách trường dữ liệu
                            </span>
                        </div>
                    </div>
                }
                open={templatePickerVisible}
                onCancel={() => setTemplatePickerVisible(false)}
                footer={null}
                width={700}
                centered
            >
                <Spin spinning={templatesLoading}>
                    {availableTemplates.length === 0 ? (
                        <Empty description="Chưa có mẫu biểu mẫu nào" />
                    ) : (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
                                gap: 14,
                                paddingTop: 8,
                            }}
                        >
                            {availableTemplates.map((tpl) => {
                                const tplFields = tpl.content?.fields || [];
                                return (
                                    <div
                                        key={tpl.uuid || tpl.name}
                                        style={{
                                            background: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderTop: '3px solid #3b82f6',
                                            borderRadius: 10,
                                            padding: 16,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.05)',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <div>
                                            {/* Card Header */}
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div
                                                        style={{
                                                            width: 34,
                                                            height: 34,
                                                            borderRadius: 8,
                                                            background: '#eff6ff',
                                                            border: '1px solid #bfdbfe',
                                                            color: '#2563eb',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <HiOutlineClipboardList size={18} />
                                                    </div>
                                                    <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.35 }}>
                                                        {tpl.name}
                                                    </span>
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: '#2563eb',
                                                        background: '#eff6ff',
                                                        border: '1px solid #bfdbfe',
                                                        borderRadius: 12,
                                                        padding: '2px 8px',
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {tplFields.length} trường
                                                </span>
                                            </div>

                                            {/* Card Description */}
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: '#64748b',
                                                    marginTop: 8,
                                                    lineHeight: 1.45,
                                                }}
                                            >
                                                {tpl.description || 'Không có mô tả'}
                                            </div>

                                            {/* Field Preview Chips */}
                                            {tplFields.length > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: 10,
                                                        background: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: 8,
                                                        padding: '8px 10px',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            fontSize: 10,
                                                            fontWeight: 600,
                                                            color: '#64748b',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.03em',
                                                            marginBottom: 6,
                                                        }}
                                                    >
                                                        Các trường dữ liệu mẫu:
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                        {tplFields.slice(0, 4).map((f) => {
                                                            const typeMeta = getFieldTypeMeta(f.type);
                                                            return (
                                                                <span
                                                                    key={f.id}
                                                                    style={{
                                                                        fontSize: 11,
                                                                        color: '#1e293b',
                                                                        background: '#ffffff',
                                                                        border: '1px solid #cbd5e1',
                                                                        borderRadius: 6,
                                                                        padding: '2px 8px',
                                                                        fontWeight: 500,
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 5,
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            color: typeMeta.color,
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            fontSize: 11,
                                                                        }}
                                                                    >
                                                                        {typeMeta.icon}
                                                                    </span>
                                                                    <span>{f.label}</span>
                                                                    <span style={{ color: '#64748b', fontWeight: 400, fontSize: 10 }}>
                                                                        ({typeMeta.label})
                                                                    </span>
                                                                </span>
                                                            );
                                                        })}
                                                        {tplFields.length > 4 && (
                                                            <span
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: '#2563eb',
                                                                    background: '#eff6ff',
                                                                    borderRadius: 6,
                                                                    padding: '2px 6px',
                                                                }}
                                                            >
                                                                +{tplFields.length - 4} trường khác
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Action Button */}
                                        <Button
                                            type="primary"
                                            block
                                            onClick={() => handleApplyTemplate(tpl)}
                                            style={{
                                                borderRadius: 6,
                                                background: '#3b82f6',
                                                fontWeight: 500,
                                                height: 34,
                                            }}
                                        >
                                            Sử dụng mẫu này
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Spin>
            </Modal>
        </Spin>
    );
};
